import { useEffect, useState } from 'react';
import { Box, Card, CardBody, CardHeader, DataTable, Select } from 'grommet';
import { Filter } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { DataTableHeader, DataTableItem, FooterNav, HeaderNav, Text } from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import { useDashboardStore } from '/src/context';

const FuseAdminDashboardReports = observer(() => {
  const dashboardStore = useDashboardStore();
  const [activeTab, setActiveTab] = useState('financial');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');
  const [selectedEquipment, setSelectedEquipment] = useState('All Equipment');
  const [selectedQuarter, setSelectedQuarter] = useState('Q2 2023');

  useEffect(() => {
    dashboardStore.getAdminView();
    return () => dashboardStore.clearDashboardData();
  }, []);

  interface FinancialData {
    facility: string;
    location: string;
    revenue: string;
    expenses: string;
    profit: string;
  }

  // Financial data
  const financialData: FinancialData[] = [
    {
      facility: 'West Coast Facility',
      location: 'California',
      revenue: '$125,000',
      expenses: '$85,000',
      profit: '$40,000',
    },
    { facility: 'East Coast Hub', location: 'New York', revenue: '$143,000', expenses: '$98,000', profit: '$45,000' },
    { facility: 'Midwest Center', location: 'Illinois', revenue: '$98,000', expenses: '$67,000', profit: '$31,000' },
    { facility: 'Southern Depot', location: 'Texas', revenue: '$112,000', expenses: '$76,000', profit: '$36,000' },
    {
      facility: 'Northwest Station',
      location: 'Washington',
      revenue: '$86,000',
      expenses: '$58,000',
      profit: '$28,000',
    },
  ];

  interface TabButtonProps {
    label: string;
    isActive: boolean;
    onClick: () => void;
  }

  const TabButton = ({ label, isActive, onClick }: TabButtonProps) => (
    <Box
      pad={{ vertical: 'small', horizontal: 'medium' }}
      onClick={onClick}
      hoverIndicator={{ color: 'light-2', opacity: 'weak' }}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Text
        color={isActive ? 'brand' : 'dark-3'}
        size="small"
        weight={isActive ? 'bold' : 'normal'}
        style={{
          letterSpacing: '0.2px',
        }}
      >
        {label}
      </Text>
    </Box>
  );

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                Reports{' '}
                <Text as="span" size="xlarge" color="status-critical" weight="normal">
                  Mock Data
                </Text>
              </Text>
            </Box>

            {/* Filters Section */}
            <Card
              background="white"
              pad="medium"
              margin={{ bottom: 'medium', vertical: 'large' }}
              elevation="none"
              round="xsmall"
            >
              <Box gap="small">
                <Text size="large" weight="normal">
                  Filter Reports
                </Text>
                <Text size="small" color="dark-6">
                  Filter reports by location, equipment type, and quarter.
                </Text>
                <Box
                  direction="row"
                  gap="medium"
                  margin={{ top: 'small' }}
                  wrap={false}
                  align="center"
                  justify="between"
                  width="100%"
                >
                  <Box basis="30%" flex={false}>
                    <Select
                      icon={<Filter size="small" />}
                      value={selectedLocation}
                      options={['All Locations', 'California', 'New York', 'Illinois', 'Texas', 'Washington']}
                      onChange={({ option }) => setSelectedLocation(option)}
                      placeholder="Select Location"
                      plain
                      dropProps={{
                        round: 'xsmall',
                        margin: { top: 'xsmall' },
                      }}
                      style={{
                        background: '#F8F9FA',
                        padding: '12px 16px',
                        borderRadius: '4px',
                        border: '1px solid #E9ECEF',
                        width: '100%',
                        height: '44px',
                      }}
                    />
                  </Box>
                  <Box basis="30%" flex={false}>
                    <Select
                      icon={<Filter size="small" />}
                      value={selectedEquipment}
                      options={['All Equipment', 'Type A', 'Type B', 'Type C']}
                      onChange={({ option }) => setSelectedEquipment(option)}
                      placeholder="Select Equipment"
                      plain
                      dropProps={{
                        round: 'xsmall',
                        margin: { top: 'xsmall' },
                      }}
                      style={{
                        background: '#F8F9FA',
                        padding: '12px 16px',
                        borderRadius: '4px',
                        border: '1px solid #E9ECEF',
                        width: '100%',
                        height: '44px',
                      }}
                    />
                  </Box>
                  <Box basis="30%" flex={false}>
                    <Select
                      value={selectedQuarter}
                      options={['Q1 2023', 'Q2 2023', 'Q3 2023', 'Q4 2023']}
                      onChange={({ option }) => setSelectedQuarter(option)}
                      placeholder="Select Quarter"
                      plain
                      dropProps={{
                        round: 'xsmall',
                        margin: { top: 'xsmall' },
                      }}
                      style={{
                        background: '#F8F9FA',
                        padding: '12px 16px',
                        borderRadius: '4px',
                        border: '1px solid #E9ECEF',
                        width: '100%',
                        height: '44px',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Card>

            {/* Tabs Navigation */}
            <Card background="white" elevation="none" round="xsmall">
              <Box direction="row">
                <TabButton
                  label="Financial Reports"
                  isActive={activeTab === 'financial'}
                  onClick={() => setActiveTab('financial')}
                />
                <TabButton
                  label="Equipment Reports"
                  isActive={activeTab === 'equipment'}
                  onClick={() => setActiveTab('equipment')}
                />
                <TabButton
                  label="Facility Reports"
                  isActive={activeTab === 'facility'}
                  onClick={() => setActiveTab('facility')}
                />
              </Box>
            </Card>

            {/* Financial Reports Table */}
            {activeTab === 'financial' && (
              <Card margin={{ vertical: 'medium' }} background="white" elevation="none" round="xsmall">
                <CardHeader
                  pad={{ horizontal: 'medium', vertical: 'medium' }}
                  border={{ side: 'bottom', color: 'light-4', size: '1px' }}
                >
                  <Box>
                    <Text size="large" weight="normal">
                      Financial Report
                    </Text>
                    <Text size="small" color="dark-6" margin={{ top: 'xsmall' }}>
                      Financial performance across facilities for {selectedQuarter}
                    </Text>
                  </Box>
                </CardHeader>
                <CardBody pad="none">
                  <DataTable
                    pad={{ horizontal: '1.5rem' }}
                    columns={[
                      {
                        property: 'facility',
                        header: <DataTableHeader toUpperCase>Facility</DataTableHeader>,
                        render: (datum: FinancialData) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.facility}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'location',
                        header: <DataTableHeader toUpperCase>Location</DataTableHeader>,
                        render: (datum: FinancialData) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.location}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'revenue',
                        header: <DataTableHeader toUpperCase>Revenue</DataTableHeader>,
                        render: (datum: FinancialData) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.revenue}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'expenses',
                        header: <DataTableHeader toUpperCase>Expenses</DataTableHeader>,
                        render: (datum: FinancialData) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.expenses}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'profit',
                        header: <DataTableHeader toUpperCase>Profit</DataTableHeader>,
                        render: (datum: FinancialData) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.profit}</DataTableItem>
                          </Box>
                        ),
                      },
                    ]}
                    data={financialData}
                    background={['light-6', 'white']}
                    border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                  />
                </CardBody>
              </Card>
            )}

            {/* Equipment Reports Tab Content */}
            {activeTab === 'equipment' && (
              <Box margin={{ top: 'medium' }}>
                <Text>Equipment reports content will go here</Text>
              </Box>
            )}

            {/* Facility Reports Tab Content */}
            {activeTab === 'facility' && (
              <Box margin={{ top: 'medium' }}>
                <Text>Facility reports content will go here</Text>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>
    </Box>
  );
});

export default FuseAdminDashboardReports;
